import React, { useState } from "react";
import { Modal, ScrollView, StyleSheet, View } from "react-native";

import FilterCard from "./FilterCard";

import ModalHeader from "@/Components/Modals/ModalHeader";
import { EventAttributeType } from "@/schemas/events/EventAttributeSet";
import {
  EventCategory,
  EventCategorySet,
} from "@/schemas/events/filter/attributes/EventCategorySet";
import {
  EventLocation,
  EventLocationSet,
} from "@/schemas/events/filter/attributes/EventLocationSet";
import {
  EventStatus,
  EventStatusSet,
} from "@/schemas/events/filter/attributes/EventStatusSet";
import {
  type EventFilterCriteria,
  InitialEventFilterCriteria,
} from "@/schemas/events/filter/EventFilterCriteria";
import { type EventFilterOption } from "@/schemas/events/filter/EventFilterOption";
import { type FilterBySelection } from "@/schemas/screen/filters/FilterBySelection";

interface FilterModalProps {
  isModalVisible: boolean;
  onApplyFilters: (filters: EventFilterCriteria) => void;
  onClose: () => void;
}

function FilterModal({
  isModalVisible,
  onApplyFilters,
  onClose,
}: FilterModalProps): React.JSX.Element {
  const [filters, setFilters] = useState<EventFilterCriteria>(
    InitialEventFilterCriteria,
  );

  const filterOptions: EventFilterOption[] = [
    {
      rank: 1,
      icon: "🏷️",
      label: "Category",
      attribute: EventCategorySet,
    },
    {
      rank: 2,
      icon: "🚦",
      label: "Status",
      attribute: EventStatusSet,
    },
    {
      rank: 3,
      icon: "📍",
      label: "Location",
      attribute: EventLocationSet,
    },
  ];

  const updateFilters = <T,>(
    newSelection: FilterBySelection<T>,
    type: EventAttributeType,
  ): void => {
    switch (type) {
      case EventAttributeType.CATEGORY:
        setFilters({
          ...filters,
          category: newSelection as FilterBySelection<EventCategory>,
        });
        break;
      case EventAttributeType.STATUS:
        setFilters({
          ...filters,
          status: newSelection as FilterBySelection<EventStatus>,
        });
        break;
      case EventAttributeType.LOCATION:
        setFilters({
          ...filters,
          location: newSelection as FilterBySelection<EventLocation>,
        });
        break;
    }
  };

  const onCloseFilterModal = (): void => {
    onApplyFilters(filters);
    onClose();
  };

  return (
    <Modal visible={isModalVisible} transparent>
      <View style={styles.modalContainer}>
        <View style={styles.filterContainer}>
          <ModalHeader title="Filters" onClose={onCloseFilterModal} />
          <ScrollView>
            {filterOptions
              .sort((a, b) => a.rank - b.rank)
              .map((option) => {
                switch (option.attribute.id) {
                  case EventAttributeType.CATEGORY:
                    return (
                      <FilterCard
                        key={option.attribute.id}
                        selection={filters.category}
                        onSetFilters={updateFilters}
                        option={option}
                      />
                    );
                  case EventAttributeType.STATUS:
                    return (
                      <FilterCard
                        key={option.attribute.id}
                        selection={filters.status}
                        onSetFilters={updateFilters}
                        option={option}
                      />
                    );
                  case EventAttributeType.LOCATION:
                    return (
                      <FilterCard
                        key={option.attribute.id}
                        selection={filters.location}
                        onSetFilters={updateFilters}
                        option={option}
                      />
                    );
                }
              })}
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  modalContainer: {
    flex: 1,
    justifyContent: "flex-end",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
  },
  filterContainer: {
    backgroundColor: "white",
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    padding: 20,
    maxHeight: "80%",
  },
});

export default FilterModal;
